"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { useLanguage } from "../context/LanguageContext"
import { useNotification } from "../context/NotificationContext"
import "./EditFarm.css"

const EditFarm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [countries, setCountries] = useState([])
  const [districts, setDistricts] = useState([])
  const [filteredDistricts, setFilteredDistricts] = useState([])

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    country: "",
    district: "",
    area: "",
    length: "",
    width: "",
    description: "",
    location: {
      type: "Point",
      coordinates: [0, 0],
    },
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch farm data
        const farmRes = await axios.get(`${API_URL}/api/farms/${id}`)

        // Fetch countries
        const countriesRes = await axios.get(`${API_URL}/api/locations/countries`)
        setCountries(countriesRes.data)

        // Fetch districts
        // const districtsRes = await axios.get(`${API_URL}/api/locations/districts`)
        // setDistricts(districtsRes.data)

        // Set form data from farm
        const farm = farmRes.data
        setFormData({
          name: farm.name || "",
          code: farm.code || "",
          country: farm.country._id || "",
          district: farm.district._id || "",
          area: farm.area || "",
          length: farm.length || "",
          width: farm.width || "",
          description: farm.description || "",
          location: farm.location || {
            type: "Point",
            coordinates: [0, 0],
          },
        })

        // Filter districts based on selected country
        if (farm.country._id) {
          const filtered = districtsRes.data.filter((district) => district.country === farm.country._id)
          setFilteredDistricts(filtered)
        }

        setLoading(false)
      } catch (err) {
        console.error("Error fetching data:", err)
        setError(err.response?.data?.msg || "Failed to load farm data")
        setLoading(false)
      }
    }

    fetchData()
  }, [id])

  const handleChange = (e) => {
    const { name, value } = e.target

    if (name === "country") {
      // Filter districts based on selected country
      const filtered = districts.filter((district) => district.country === value)
      setFilteredDistricts(filtered)

      // Reset district if country changes
      setFormData({
        ...formData,
        [name]: value,
        district: "",
      })
    } else if (name === "area" || name === "length" || name === "width") {
      // Convert to number
      setFormData({
        ...formData,
        [name]: value === "" ? "" : Number(value),
      })
    } else {
      setFormData({
        ...formData,
        [name]: value,
      })
    }
  }

  const handleCoordinateChange = (e) => {
    const { name, value } = e.target
    const index = name === "longitude" ? 0 : 1
    const newCoordinates = [...formData.location.coordinates]
    newCoordinates[index] = Number(value)

    setFormData({
      ...formData,
      location: {
        ...formData.location,
        coordinates: newCoordinates,
      },
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    if (!formData.name || !formData.country || !formData.district) {
      showNotification(t("Name, country, and district are required"), "error")
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      // Update farm
      await axios.put(`${API_URL}/api/farms/${id}`, formData)

      showNotification(t("Farm updated successfully"), "success")
      navigate(`/farms/${id}`)
    } catch (err) {
      console.error("Error updating farm:", err)
      setError(err.response?.data?.msg || "Failed to update farm")
      showNotification(err.response?.data?.msg || "Failed to update farm", "error")
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="edit-farm-loading">
        <div className="spinner"></div>
        <p>{t("Loading farm data...")}</p>
      </div>
    )
  }

  if (error && !formData.name) {
    return (
      <div className="edit-farm-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{t(error)}</p>
        <button onClick={() => navigate("/farm-management")} className="back-button">
          {t("Back to Farm Management")}
        </button>
      </div>
    )
  }

  return (
    <div className="edit-farm-container" dir={direction}>
      <div className="edit-farm-header">
        <h1>{t("Edit Farm")}</h1>
        <p>{t("Update your farm information")}</p>
      </div>

      <form onSubmit={handleSubmit} className="edit-farm-form">
        <div className="form-section">
          <h2>{t("Basic Information")}</h2>

          <div className="form-group">
            <label htmlFor="name">{t("Farm Name")} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t("Enter farm name")}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="code">{t("Farm Code")}</label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder={t("Farm code (auto-generated if empty)")}
              disabled
            />
            <small>{t("Farm code cannot be changed")}</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="country">{t("Country")} *</label>
              <select id="country" name="country" value={formData.country} onChange={handleChange} required>
                <option value="">{t("Select a country")}</option>
                {countries.map((country) => (
                  <option key={country._id} value={country._id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="district">{t("District")} *</label>
              <select
                id="district"
                name="district"
                value={formData.district}
                onChange={handleChange}
                required
                disabled={!formData.country}
              >
                <option value="">{t("Select a district")}</option>
                {filteredDistricts.map((district) => (
                  <option key={district._id} value={district._id}>
                    {district.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t("Farm Dimensions")}</h2>

          <div className="form-group">
            <label htmlFor="area">{t("Total Area (m²)")} *</label>
            <input
              type="number"
              id="area"
              name="area"
              value={formData.area}
              onChange={handleChange}
              placeholder={t("Enter farm area in square meters")}
              min="0"
              step="0.01"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="length">{t("Length (m)")}</label>
              <input
                type="number"
                id="length"
                name="length"
                value={formData.length}
                onChange={handleChange}
                placeholder={t("Enter length in meters")}
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="width">{t("Width (m)")}</label>
              <input
                type="number"
                id="width"
                name="width"
                value={formData.width}
                onChange={handleChange}
                placeholder={t("Enter width in meters")}
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>{t("Location")}</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="longitude">{t("Longitude")}</label>
              <input
                type="number"
                id="longitude"
                name="longitude"
                value={formData.location.coordinates[0]}
                onChange={handleCoordinateChange}
                placeholder={t("Enter longitude")}
                step="0.000001"
              />
            </div>

            <div className="form-group">
              <label htmlFor="latitude">{t("Latitude")}</label>
              <input
                type="number"
                id="latitude"
                name="latitude"
                value={formData.location.coordinates[1]}
                onChange={handleCoordinateChange}
                placeholder={t("Enter latitude")}
                step="0.000001"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">{t("Farm Description")}</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={t("Enter a description of your farm")}
              rows="4"
            ></textarea>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate(`/farms/${id}`)}
            className="cancel-button"
            disabled={submitting}
          >
            {t("Cancel")}
          </button>
          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner-small"></span>
                {t("Updating...")}
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                {t("Update Farm")}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default EditFarm
